import { useQuery, type QueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { getDisplayLogoUrl } from '@/lib/logoUrl';

export const RESOLVE_CANONICAL_ORGANIZATION_LOGO_RPC = 'resolve_ent5657_canonical_organization_logo';
export const RESOLVE_CANONICAL_ORGANIZATION_LOGOS_RPC = 'resolve_ent5657_canonical_organization_logos';
export const CAN_REMOVE_CANONICAL_ORGANIZATION_LOGO_UPLOAD_RPC = 'can_remove_ent5657_organization_logo_upload';

export type CanonicalOrganizationLogoSource =
  | 'organization_logo'
  | 'organization_upload'
  | 'claim_linked_listing_upload'
  | 'claim_linked_listing_discovery'
  | 'organization_discovery'
  | 'legacy_preserved_display'
  | 'initials';

export interface CanonicalOrganizationLogo {
  organizationId: string;
  /** Null exactly when `logoSource` is `initials`. */
  logoUrl: string | null;
  logoSource: CanonicalOrganizationLogoSource;
  marketplaceListingId: string | null;
  listingDomain: string | null;
  /** Permission-aware management state, deliberately not provenance. */
  canRemoveUpload: boolean;
}

const CANONICAL_SOURCES: ReadonlySet<string> = new Set([
  'organization_logo',
  'organization_upload',
  'claim_linked_listing_upload',
  'claim_linked_listing_discovery',
  'organization_discovery',
  'legacy_preserved_display',
  'initials',
]);

export function canonicalOrganizationLogoQueryKey(organizationId: string) {
  return ['canonical-organization-logo', organizationId] as const;
}

export function canonicalOrganizationLogoDisplayQueryKey(organizationId: string) {
  return [...canonicalOrganizationLogoQueryKey(organizationId), 'display'] as const;
}

export function canonicalOrganizationLogoManagementQueryKey(organizationId: string) {
  return [...canonicalOrganizationLogoQueryKey(organizationId), 'management'] as const;
}

function readTrimmedString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Validates one resolver row. Anything outside the contract (unknown source, a url paired with
 * initials, an upload source without a url, no row) is an error — never silently a logo or initials.
 */
export function parseCanonicalOrganizationLogo(
  organizationId: string,
  data: unknown,
): CanonicalOrganizationLogo {
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null | undefined;
  if (!row || typeof row !== 'object') {
    throw new Error('Canonical organisation logo resolver returned no result');
  }
  const logoSource = row.logo_source;
  if (typeof logoSource !== 'string' || !CANONICAL_SOURCES.has(logoSource)) {
    throw new Error('Canonical organisation logo resolver returned an invalid source');
  }
  const logoUrl = readTrimmedString(row.logo_url);
  if ((logoSource === 'initials') !== (logoUrl === null)) {
    throw new Error('Canonical organisation logo resolver returned an invalid source/url pair');
  }
  return {
    organizationId,
    logoUrl,
    logoSource: logoSource as CanonicalOrganizationLogoSource,
    marketplaceListingId: readTrimmedString(row.marketplace_listing_id),
    listingDomain: readTrimmedString(row.listing_domain),
    canRemoveUpload: row.can_remove_upload === true,
  };
}

/**
 * Resolves a bounded collection through the same server projection as the
 * single-org hook. The server deduplicates IDs and runs the authorization
 * check for each requested resource; absent rows are a contract failure.
 */
export async function fetchCanonicalOrganizationLogos(
  organizationIds: readonly string[],
): Promise<Map<string, CanonicalOrganizationLogo>> {
  const uniqueIds = [...new Set(organizationIds.filter((organizationId) => organizationId.trim().length > 0))];
  if (uniqueIds.length === 0) return new Map();

  const logos = new Map<string, CanonicalOrganizationLogo>();
  // PostgREST caps function results too. Serial bounded requests avoid both
  // truncation and unbounded fan-out for large MSP customer collections.
  const batchSize = 500;
  for (let offset = 0; offset < uniqueIds.length; offset += batchSize) {
    const batchIds = new Set(uniqueIds.slice(offset, offset + batchSize));
    const { data, error } = await supabase.rpc(RESOLVE_CANONICAL_ORGANIZATION_LOGOS_RPC, {
      p_organization_ids: [...batchIds],
    });
    if (error) throw error;
    if (!Array.isArray(data)) {
      throw new Error('Canonical organisation logo batch resolver returned an invalid result');
    }
    const receivedIds = new Set<string>();
    for (const row of data) {
      const organizationId = typeof row?.organization_id === 'string' ? row.organization_id : null;
      if (!organizationId || !batchIds.has(organizationId) || receivedIds.has(organizationId)) {
        throw new Error('Canonical organisation logo batch resolver returned an invalid organisation');
      }
      logos.set(organizationId, parseCanonicalOrganizationLogo(organizationId, row));
      receivedIds.add(organizationId);
    }
    if (receivedIds.size !== batchIds.size) {
      throw new Error('Canonical organisation logo batch resolver returned an incomplete result');
    }
  }
  return logos;
}

/**
 * The display URL for a resolved canonical mark: the resolver's own asset (host-normalised and
 * cache-busted), or null for initials. No domain is passed, so there is never a favicon fallback.
 */
export function getCanonicalOrganizationLogoDisplayUrl(
  logo: CanonicalOrganizationLogo | null | undefined,
  cacheBustKey?: string | null,
): string | null {
  if (!logo || logo.logoSource === 'initials') return null;
  return getDisplayLogoUrl({ primaryUrl: logo.logoUrl, cacheBustKey: cacheBustKey ?? null });
}

export async function fetchCanonicalOrganizationLogo(
  organizationId: string,
  options: { includeManagement?: boolean } = {},
): Promise<CanonicalOrganizationLogo> {
  const display = await supabase.rpc(RESOLVE_CANONICAL_ORGANIZATION_LOGO_RPC, {
    p_organization_id: organizationId,
  });
  if (display.error) throw display.error;
  const canonicalLogo = parseCanonicalOrganizationLogo(organizationId, display.data);
  if (!options.includeManagement) return canonicalLogo;

  const management = await supabase.rpc(CAN_REMOVE_CANONICAL_ORGANIZATION_LOGO_UPLOAD_RPC, {
    p_organization_id: organizationId,
  });
  if (management.error) throw management.error;
  if (typeof management.data !== 'boolean') {
    throw new Error('Canonical organisation logo management resolver returned an invalid result');
  }
  return {
    ...canonicalLogo,
    canRemoveUpload: management.data,
  };
}

/**
 * ENT-5657 / D7 option C: the single client read of an organisation's canonical mark
 * (organisation upload → typed claim-linked listing upload → verified-domain,
 * first-party discovered asset → initials), resolved and authorised server-side.
 * Every workspace-mark surface uses this hook so they cannot diverge.
 */
export function useCanonicalOrganizationLogo(
  organizationId: string | null | undefined,
  options: { enabled?: boolean; includeManagement?: boolean } = {},
) {
  const enabled = (options.enabled ?? true) && Boolean(organizationId);
  const includeManagement = options.includeManagement === true;
  return useQuery({
    queryKey: includeManagement
      ? canonicalOrganizationLogoManagementQueryKey(organizationId ?? '')
      : canonicalOrganizationLogoDisplayQueryKey(organizationId ?? ''),
    queryFn: () => fetchCanonicalOrganizationLogo(organizationId as string, { includeManagement }),
    enabled,
    staleTime: 60_000,
  });
}

export function canonicalOrganizationLogosQueryKey(organizationIds: readonly string[]) {
  return ['canonical-organization-logos', ...[...new Set(organizationIds)].sort()] as const;
}

/**
 * ENT-6441: one bounded batch read of the canonical marks for a visible collection (one table
 * page), so a list never resolves logos row by row. Keyed by the sorted id set, so re-ordering
 * the same page reuses the cached result and a page change issues exactly one new call.
 */
export function useCanonicalOrganizationLogos(
  organizationIds: readonly string[],
  options: { enabled?: boolean } = {},
) {
  const queryKey = canonicalOrganizationLogosQueryKey(organizationIds);
  const ids = queryKey.slice(1) as string[];
  return useQuery({
    queryKey,
    queryFn: () => fetchCanonicalOrganizationLogos(ids),
    enabled: (options.enabled ?? true) && ids.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}

type CanonicalOrganizationLogoWriteListener = (organizationId: string) => void;
const canonicalOrganizationLogoWriteListeners = new Set<CanonicalOrganizationLogoWriteListener>();

/**
 * ENT-6445: readers that cache canonical marks outside react-query (the `useMSPAccess` module
 * cache) subscribe here, so a logo write reaches them through the same invalidation call.
 */
export function subscribeCanonicalOrganizationLogoWrites(listener: CanonicalOrganizationLogoWriteListener) {
  canonicalOrganizationLogoWriteListeners.add(listener);
  return () => {
    canonicalOrganizationLogoWriteListeners.delete(listener);
  };
}

/**
 * ENT-6441: after a logo write, refresh every cached read of that organisation's mark: the single
 * display/management/choices/provenance reads (one key prefix) and any batch page containing it.
 * ENT-6445: subscribed non-react-query readers are notified first.
 */
export async function invalidateCanonicalOrganizationLogo(queryClient: QueryClient, organizationId: string) {
  for (const listener of canonicalOrganizationLogoWriteListeners) listener(organizationId);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: canonicalOrganizationLogoQueryKey(organizationId) }),
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'canonical-organization-logos' && query.queryKey.includes(organizationId),
    }),
  ]);
}
